<script setup lang="ts">
  import { computed } from 'vue';
  import { PostOrderDisplayMode } from '@/enums/PostOrderDisplayMode';
  import { IBranch } from '@/interfaces/IBranch';
  import { i18n } from '@/plugins/i18n';
  import SettingsBranchMarkdownEditor from './SettingsBranchMarkdownEditor.vue';
  import {
    getBranchPostOrderModeOptions,
    getBranchPostOrderSectionConfig,
    getBranchPostOrderStatusHint,
    getPostOrderCustomMessageLength,
    POST_ORDER_CUSTOM_MESSAGE_MAX_LENGTH,
    shouldShowPostOrderCustomEditor,
    TBranchPostOrderSectionKey,
  } from './post-order-display.utils';

  const props = withDefaults(
    defineProps<{
      editingBranch: IBranch;
      orderTypeKey: TBranchPostOrderSectionKey;
      errorMessage?: string | null;
    }>(),
    {
      errorMessage: null,
    }
  );

  const emit = defineEmits<{
    (e: 'change'): void;
  }>();

  const t = (key: string, params?: Record<string, unknown>): string =>
    String(i18n.t(key, params));

  const sectionConfig = computed(() =>
    getBranchPostOrderSectionConfig(props.orderTypeKey)
  );

  const modeOptions = computed(() =>
    getBranchPostOrderModeOptions(props.orderTypeKey, t)
  );

  const selectedMode = computed({
    get: () =>
      props.editingBranch[sectionConfig.value.modeKey] ??
      PostOrderDisplayMode.Status,
    set: (value: PostOrderDisplayMode) => {
      props.editingBranch[sectionConfig.value.modeKey] = value;
      emit('change');
    },
  });

  const selectedHint = computed(
    () =>
      modeOptions.value.find((option) => option.value === selectedMode.value)
        ?.hint ?? ''
  );

  const customMessage = computed({
    get: () => props.editingBranch[sectionConfig.value.customMessageKey] ?? '',
    set: (value: string) => {
      props.editingBranch[sectionConfig.value.customMessageKey] = value;
      emit('change');
    },
  });

  const customMessageLength = computed(() =>
    getPostOrderCustomMessageLength(
      props.editingBranch[sectionConfig.value.customMessageKey]
    )
  );

  const showEditor = computed(() =>
    shouldShowPostOrderCustomEditor(selectedMode.value)
  );

  const statusHint = computed(() =>
    getBranchPostOrderStatusHint(props.editingBranch, props.orderTypeKey, t)
  );
</script>

<template>
  <div class="post-order-setting">
    <p class="post-order-setting__title">
      {{ $t(sectionConfig.titleKey) }}
    </p>

    <v-select
      v-model="selectedMode"
      :items="modeOptions"
      item-text="text"
      item-value="value"
      :hint="selectedHint"
      persistent-hint
      outlined
      dense
      hide-details="auto"
    />

    <p v-if="statusHint" class="post-order-setting__status-hint">
      <v-icon x-small color="grey">mdi-information-outline</v-icon>
      {{ statusHint }}
    </p>

    <div v-if="showEditor" class="post-order-setting__editor">
      <SettingsBranchMarkdownEditor
        :value="customMessage"
        :error-message="errorMessage"
        :max-length="POST_ORDER_CUSTOM_MESSAGE_MAX_LENGTH"
        :character-count="customMessageLength"
        @input="customMessage = $event"
        @change="emit('change')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .post-order-setting {
    @apply tw-space-y-3 tw-mt-2;

    &__title {
      @apply tw-mb-0 tw-text-sm tw-font-medium tw-text-neutral-600;
    }

    &__status-hint {
      @apply tw-mb-0 tw-text-xs tw-leading-5 tw-text-neutral-400;
    }

    &__editor {
      @apply tw-space-y-2;
    }
  }
</style>
